<template>
  <div :style="layoutStyle">
    <div class="review-tags-settings-list-item">
      <v-text-field
        v-if="editing"
        v-model="tagLabel"
        class="rtsli-text-field"
        :class="{ error: errorMsg }"
        :color="errorMsg ? 'error' : 'primary'"
        placeholder="Tag Name"
        hide-details
        dense
        autofocus
        outlined
        rounded
        :error="!!errorMsg"
        :disabled="disabled"
        @keyup.enter="updateTag"
        @keyup.escape="cancelEdit"
      />
      <reference-tag-chip
        v-else
        class="rstp-chip"
        :label="tag.label"
      />

      <icon-button
        v-if="editing"
        class="small-button"
        size="x-small"
        icon="check"
        color="success"
        icon-color="white"
        :disabled="disabled"
        @click="updateTag()"
      />
      <edit-button
        v-else-if="canEdit"
        class="small-button"
        size="x-small"
        outlined
        :disabled="disabled"
        @click="requestEdit()"
      />

      <icon-button
        v-if="editing"
        class="small-button"
        size="x-small"
        :icon="newRow ? 'remove' : 'clear'"
        color="error"
        icon-color="white"
        :disabled="disabled"
        @click="cancelEdit()"
      />
      <icon-button
        v-else-if="canEdit"
        class="small-button"
        size="x-small"
        outlined
        icon="delete"
        :disabled="disabled"
        @click="deleteTag()"
      />
    </div>
    <template v-if="editing && errorMsg">
      <v-tooltip v-if="small" bottom color="error">
        <template #activator="{ on, attrs }">
          <v-icon v-bind="attrs" color="error" v-on="on"
            >error</v-icon
          >
        </template>
        <span>{{ errorMsg }}</span>
      </v-tooltip>
      <v-chip v-else class="font-weight-bold" color="error" small>
        <v-icon small left>error</v-icon>
        <span>{{ errorMsg }}</span>
      </v-chip>
    </template>
  </div>
</template>

<script>
import { Tag } from '@/models';
import ReferenceTagChip from '@/components/References/ReferenceTagChip';
import EditButton from '@/components/Common/EditButton';
import IconButton from '@/components/Common/IconButton';

export default {
  name: 'ReviewTagsSettingsListItem',
  components: {
    ReferenceTagChip,
    EditButton,
    IconButton,
  },
  props: {
    tag: {
      type: Tag,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    newRow: {
      type: Boolean,
      default: false,
    },
    errorMsg: {
      type: String,
      required: false,
      default: '',
    },
    bodyWidth: {
      type: String,
      default: '20rem',
    },
    small: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'createTag',
    'updateTag',
    'deleteTag',
    'requestEdit',
    'cancelEdit',
  ],
  data: () => ({
    tagLabel: '',
  }),
  computed: {
    layoutStyle() {
      return {
        display: 'grid',
        'grid-gap': this.small ? '1rem' : '1.5rem',
        'grid-template-columns': `${this.bodyWidth} minmax(24px, 1fr)`,
        'align-items': 'center',
      };
    },
  },
  watch: {
    editing() {
      this.setEditString();
    },
  },
  created() {
    this.setEditString();
  },
  methods: {
    setEditString() {
      this.tagLabel = this?.tag?.label ?? '';
    },
    createTag() {
      this.$emit('createTag');
    },
    deleteTag() {
      this.$emit('deleteTag');
    },
    requestEdit() {
      this.$emit('requestEdit');
    },
    updateTag() {
      const newTag = new Tag(this.tag);
      newTag.label = this.tagLabel;
      this.$emit('updateTag', newTag);
    },
    cancelEdit() {
      this.tagLabel = this.tag.label;
      this.$emit('cancelEdit');
    },
  },
};
</script>

<style scoped lang="scss">
$icon-size: 30px;
$row-height: 26px;

.review-tags-settings-list-item {
  display: grid;
  grid-template-columns: 1fr $icon-size $icon-size;
  grid-template-rows: $row-height;
  grid-gap: 0.5rem;
  align-items: center;
  justify-items: start;
}

::v-deep .v-input__control {
  background-color: #ffffff;
}

::v-deep .v-input__slot {
  padding: 0 16px !important;
  min-height: 18px !important;
}

::v-deep .v-text-field__slot > input {
  color: var(--v-primary-base) !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep .v-text-field.error input {
  color: var(--v-error-base) !important;
}

.small-button {
  height: $icon-size;
  width: $icon-size;
}
</style>
